import { CharacterType } from "../../game-data/characters"
import { MutatorShortName, MUTATOR_DEFINITIONS } from "../../mutators/mutator-definitions"
import { PrimaryWeaponType } from "../../weapons/weapon-types"
import { MapNames } from "../../world-generation/world-data"
import { UpgradeCollectionName } from "../upgrade-definitions"
import { MetaPerkName } from "./meta-perk-name"

// These names match something in Griddle!! Can't be changed willy nilly
export enum MetaUnlockTag {
    Bow = 'bow',
    Spear = 'spear',
		Cannon = 'cannon',
    Gernerd = 'gernerd',
    Bees = 'bees',
    Lute = 'lute',
    Story7 = '7chapter',
    Story10 = '10chapter',
    Story30 = '30chapter',
    StartTwist = 'startWithTwist',
    SplitPersonality = 'splitPersonality',
    NarrowFocus = 'narrowFocus',
    DeferredLearning = 'deferredLearning',
    GameSpeed = 'gameSpeed',
    ConeDog = 'coneDog',
    Zoomies = 'zoomies',
    PetSurvival = 'petSurvival',
    Explosions = 'explosions',
    FireFairy = 'fireFairy',
    Barbarian = 'barbarian',
    Boomerang = 'boomerang',
    NikolaScope = 'nikScope',
    ElementalMaelstrom = 'elementalMaelstrom',
    PhoenixBombs = 'phoenixBombs',


    FountainsOfMana = 'fountainsOfMana',
    LittlePlagueBearers = 'littlePlagueBearers',
    SpeedDrafting = 'speedDrafting',

    AttackOfTheGiantShamblers = 'giantShamblers',
    HardcoreSurvival = 'hardcoreSurvival',
    SpringTimeDancing = 'springTimeDancing',
    ParanormalExercise = 'paranormalExercise',
    CombatArena = 'combatArena',
    Scrapyard = 'scrapyard',
    MonsterMergeGame = 'monsterMergeGame',
    SkillfullMoves = 'skillfulMoves',
    PrestigiousTreasureTrove = 'prestigiousTreasureTrove',
    Lootstreak = 'lootstreak',
    RunningOfTheShriekers = 'runningOfTheShriekers',
    RazorSharpSnowfall = 'razorSharpSnowfall',

    ElixirOfSomething = 'elixirOfSomething',
    DontDropTheEgg = 'dontDropTheEgg',

    BetterGoFast = 'betterGoFast',
    SpectralFarmer = 'spectralFarmer',
    TemporalDistortion = 'temporalDistortion',
    RoamingWildlings = 'roamingWildlings',
    InsectFever = 'insectFever',
    SpookyGhosts = 'spookyGhosts',
    RandomRicochet = 'randomRicochet',

    Radar = 'radar',
    Solara = 'solara',
    DarkStormyNight = 'darkStormyNight',

    Tundra = 'tundra',
}

export type MetaUnlockDefinition = {
    unlockUpgradeName?: UpgradeCollectionName
    unlockPrimaryWeapon?: PrimaryWeaponType
    unlockCharacter?: CharacterType
    perkUnlock?: MetaPerkName
    twistUnlock?: MutatorShortName
    mapUnlock?: MapNames
    isSteam?: boolean

    // For anything else
    applyFunction?: () => void
    removeFunction?: () => void // not sure if needed

    // UI
    name?: string
    /** The description that shows up to explain the unlock. This is auto-generated for twist unlocks, using the twist's description from mutator-definitions.ts. */
    description?: string
    icon?: string
    mission?: string
}

export const UnlockDefinitions: Record<MetaUnlockTag, MetaUnlockDefinition> = {
    [MetaUnlockTag.Bow]: {
        unlockPrimaryWeapon: PrimaryWeaponType.Bow,
        name: "Bow",
        description: "Unlocks the bow as a primary weapon",
        mission: "Kill 100 Eyebats",
        icon: "primary-bow"
    },
    [MetaUnlockTag.Spear]: {
        unlockPrimaryWeapon: PrimaryWeaponType.Spear,
        name: "Spear",
        description: "Unlocks the spear as a primary weapon",
        mission: "Kill 200 Shriekers",
        icon: "primary-spear"
    },
    [MetaUnlockTag.Cannon]: {
      unlockPrimaryWeapon: PrimaryWeaponType.Cannon,
      name: "Cannon",
      description: "Unlocks the cannon as a primary weapon",
      mission: "Defeat the Mountain's Everfrost boss on chapter 3 or greater",

      isSteam: true,
      icon: "primary-cannon"
  },
    [MetaUnlockTag.ConeDog]: {
        unlockCharacter: CharacterType.ConeDog,
        name: "Character: Cone Dog",
        description: "Permanently unlock Cone Dog as a playable character",
        mission: "Beat a chapter with a fully evolved spear",
        icon: "character-cone-dog"
    },
    [MetaUnlockTag.SplitPersonality]: {
        twistUnlock: 'splitPersonality',
        name: "Split Personality",
        description: "",
        mission: "Beat a chapter with the Twice Dead twist",
        icon: "twist-split-personality"
    },
    [MetaUnlockTag.NarrowFocus]: {
        twistUnlock: 'narrowFocus',
        name: "Narrow Focus",
        description: "",
        mission: "Gain 40 levels in a run",
        icon: "twist-narrow-focus"
    },
    [MetaUnlockTag.DeferredLearning]: {
        twistUnlock: 'deferredLearning',
        name: "Deferred Learning",
        description: '',
        mission: "Gain 45 levels in a run",
        icon: "twist-deferred-learning"
    },
    [MetaUnlockTag.Zoomies]: {
        twistUnlock: 'zoomies',
        name: "Zoomies",
        description: '',
        mission: "Beat a chapter with all movement and attack speed diamond upgrades",
        icon: "twist-zoomies"
    },
    [MetaUnlockTag.PetSurvival]: {
        twistUnlock: 'petSurvival',
        name: "Pet Survival",
        description: '',
        mission: "Gain 7 pets in one run",
        icon: "twist-pet-survival"
    },
    [MetaUnlockTag.Explosions]: {
        twistUnlock: 'explosions',
        name: "A Shocking Number of Explosions",
        description: '',
        mission: "Inflict doom 500 times",
        icon: 'twist-a-shocking-number-of-explosions'
    },
    [MetaUnlockTag.ElementalMaelstrom]: {
        twistUnlock: 'elementalMaelstrom',
        name: "Elemental Maelstrom",
        description: '',
        mission: "Inflict an Elemental Ailment 300 times",
        icon: 'twist-elemental-maelstrom'
    },
    [MetaUnlockTag.GameSpeed]: {
        perkUnlock: MetaPerkName.GameSpeed,
        name: "Game Speed",
        description: "Unlocks the Game Speed perk",
        mission: 'Win 1 run',
        icon: 'perk-game-speed'
    },
    [MetaUnlockTag.Bees]: {
        unlockUpgradeName: 'beesUnlock',
        name: "Bees",
        mission: "Kill 1500 insects",
        description: "Permanently unlock Bees as a secondary weapon",
        icon: 'secondary-bees',
    },
    [MetaUnlockTag.Lute]: {
        unlockUpgradeName: 'luteUnlock',
        name: "Lute",
        description: 'Permanently unlock the Lute as a secondary weapon',
        mission: "Beat a chapter with two fully upgraded secondary weapons",
        icon: 'secondary-lute'
    },
    [MetaUnlockTag.Barbarian]: {
        unlockCharacter: CharacterType.Barbarian,
        name: 'Character: Barb Barian',
        description: 'Permanently unlock Barb Barian as a playable character',
        icon: 'character-barb'
    },
    [MetaUnlockTag.Boomerang]: {
        unlockPrimaryWeapon: PrimaryWeaponType.Boomerang,
        name: 'Boomerang',
        description: 'Permanently unlock the Boomerang as a primary weapon',
        icon: 'primary-boomerang'
    },
    [MetaUnlockTag.NikolaScope]: {
        unlockUpgradeName: 'nikolaScopeUnlock',
        name: `Nikola's Scope`,
        description: `Permanently unlock Nikola's Scope as a secondary weapon`,
        icon: 'secondary-nikolas-scope'
    },
    [MetaUnlockTag.PhoenixBombs]: {
        unlockUpgradeName: 'phoenixBombsUnlock',
        isSteam: true,
        name: `Phoenix Bombs`,
        description: `Permanently unlock Phoenix Bombs as a secondary weapon`,
        icon: 'secondary-phoenix-bombs'
    },
    [MetaUnlockTag.FountainsOfMana]: {
        isSteam: true,
        name: 'Fountains of Mana',
        twistUnlock: 'fountainsOfMana',
        description: 'Unlocks the Fountains of Mana Event',
        icon: 'twist-fountains-of-mana',
    },
    [MetaUnlockTag.LittlePlagueBearers]: {
        isSteam: true,
        name: 'Little Plague Bearers',
        twistUnlock: 'littlePlagueBearers',
        description: '',
        icon: 'twist-little-plague-bearers',
        mission: "Beat a chapter with the 'Elemental Maelstrom' plot twist, while taking all generic poison upgrades"
    },
    [MetaUnlockTag.SpeedDrafting]: {
        name: 'Speed Drafting',
        isSteam: true,
        twistUnlock: 'speedDrafting',
        description: '',
        icon: 'twist-speed-drafting',
    },
    [MetaUnlockTag.AttackOfTheGiantShamblers]: {
        name: 'Attack of the Giant Shamblers',
        twistUnlock: 'giantShamblers',
        description: '',
        icon: 'twist-attack-of-the-giant-shamblers',
        mission: 'Deal over 3.5 million damage with the wand in a single game'
    },
    [MetaUnlockTag.HardcoreSurvival]: {
        name: 'Hardcore Survival',
        twistUnlock: 'hardcoreSurvival',
        description: '',
        icon: 'twist-hardcore-survival',
        mission: 'Beat a chapter while collecting 3 or less heart pickups'
    },
    [MetaUnlockTag.SpringTimeDancing]: {
        name: 'Spring-Time Dancing',
        twistUnlock: 'springTimeDancing',
        description: '',
        icon: 'twist-spring-time-dancing',
        mission: `Beat a chapter with the 'Summer-Time Salsa' and 'Falling Folk Dance' plot twists active`
    },
    [MetaUnlockTag.ParanormalExercise]: {
        name: 'Paranormal Exercise',
        twistUnlock: 'paranormalExercise',
        description: '',
        icon: 'twist-paranormal-exercise'
    },
    [MetaUnlockTag.CombatArena]: {
        name: 'Combat Arena',
        twistUnlock: 'combatArena',
        description: '',
        icon: 'twist-combat-arena',
        mission: 'Beat a chapter with 5 or more plot twists while gaining 5 or more pets'
    },
    [MetaUnlockTag.Scrapyard]: {
        name: 'Scrapyard',
        isSteam: true,
        twistUnlock: 'scrapyard',
        description: '',
        icon: 'twist-scrapyard',
    },
    [MetaUnlockTag.MonsterMergeGame]: {
        name: 'Monster Merge Game',
        isSteam: true,
        twistUnlock: 'monsterMergeGame',
        description: '',
        icon: 'twist-monster-merge-game',
    },
    [MetaUnlockTag.SkillfullMoves]: {
        name: 'Skillful Moves',
        isSteam: true,
        twistUnlock: 'skillfulMoves',
        description: `Unlocks the 'Skillful Moves' twist`,
        icon: 'twist-skillfull-moves',
        mission: 'Use your skill a total of 1,500 times or greater'
    },
    [MetaUnlockTag.PrestigiousTreasureTrove]: {
        name: 'Prestigious Treasure Trove',
        isSteam: true,
        twistUnlock: 'prestigiousTreasureTrove',
        description: `Unlocks the 'Prestigious Treasure Trove' twist`,
        icon: 'twist-prestigious-treasure-trove',
        mission: `Defeat the 'Mountain' map on chapter 25 or greater`
    },
    [MetaUnlockTag.Lootstreak]: {
        name: 'Lootstreak',
        isSteam: true,
        twistUnlock: 'lootstreak',
        description: `Unlocks the 'Lootstreak' twist`,
        icon: 'twist-lootstreak',
        mission: 'Collect 50 lost scrolls and get a killstreak of 1,000 or greater in a single run'
    },
    [MetaUnlockTag.ElixirOfSomething]: {
        name: 'Elixir of... Something?',
        isSteam: true,
        twistUnlock: 'elixirOfSomething',
        description: `Unlocks the 'Elixir of... Something?' twist`,
        icon: 'twist-elixir-of-something'
    },
    [MetaUnlockTag.DontDropTheEgg]: {
        name: `Don't Drop The Egg`,
        isSteam: true,
        twistUnlock: 'dontDropTheEgg',
        description: `Unlocks the 'Don't Drop The Egg' twist`,
        icon: 'twist-dont-drop-the-egg',
        mission: 'Gain 9 or more pets in one chapter'
    },
    [MetaUnlockTag.BetterGoFast]: {
        name: `Better Go Fast`,
        isSteam: true,
        twistUnlock: 'betterGoFast',
        description: `Unlocks the 'Better Go Fast' twist`,
        icon: 'twist-better-go-fast',
        mission: 'Gain a killstreak of 2,000+ kills'
    },
    [MetaUnlockTag.SpectralFarmer]: {
        name: `Spectral Farmer`,
        isSteam: true,
        twistUnlock: 'spectralFarmer',
        description: `Unlocks the 'Spectral Farmer' twist`,
        icon: 'twist-spectral-farmer',
        mission: 'Beat a chapter with 65,000+ score'
    },
    [MetaUnlockTag.TemporalDistortion]: {
        name: `Temporal Distortion`,
        isSteam: true,
        twistUnlock: 'temporalDistortion',
        description: `Unlocks the 'Temporal Distortion' twist`,
        icon: 'twist-temporal-distortion',
        mission: 'Find and defeat the Brute Shrieker boss'
    },
    [MetaUnlockTag.Radar]: {
        name: `Character: Radar`,
        isSteam: true,
        description: `Permanently unlock Radar as a playable character`,
        unlockCharacter: CharacterType.Radar,
        icon: 'character-radar',
        mission: 'Gain 15,000 total XP'
    },
    [MetaUnlockTag.Solara]: {
        unlockCharacter: CharacterType.SolaraSun,
        isSteam: true,
        name: 'Character: Solara',
        description: 'Permanently unlock Solara as a playable character',
        icon: 'character-solara',
        mission: 'Use character skills 500 times'
    },
    [MetaUnlockTag.FireFairy]: {
        unlockUpgradeName: 'fireFairiesUnlock',
        isSteam: true,
        name: `Fire Fairy`,
        description: `Permanently unlock Fire Fairy as a secondary weapon`,
        mission: 'Inflict ignite a total of 25,000 times',
        icon: 'secondary-fire-fairy',
    },
    [MetaUnlockTag.DarkStormyNight]: {
        unlockUpgradeName: 'darkStormyNightUnlock',
        isSteam: true,
        name: `Dark Stormy Night`,
        description: `Permanently unlock Dark Stormy Night as a secondary weapon`,
        mission: 'Inflict shock a total of 300 times',
        icon: 'secondary-dark-stormy-night',
    },
    [MetaUnlockTag.RoamingWildlings]: {
        name: 'Roaming Wildlings',
        isSteam: true,
        twistUnlock: 'roamingWildlings',
        description: `Unlocks the 'Roaming Wildlings' twist`,
        icon: 'twist-roaming-wildlings',
        mission: 'Rescue 30 total pets'
    },
    [MetaUnlockTag.InsectFever]: {
        name: 'Insect Fever',
        isSteam: true,
        twistUnlock: 'insectFever',
        description: `Unlocks the 'Insect Fever' twist`,
        icon: 'twist-insect-fever',
        mission: `Kill 25 insects using the 'BEES!' weapon`
    },
    [MetaUnlockTag.SpookyGhosts]: {
        name: 'Spooky Ghosts',
        isSteam: true,
        twistUnlock: 'spookyGhosts',
        description: `Unlocks the 'Spooky Ghosts' twist`,
        icon: 'twist-spooky-ghosts',
        mission: `Beat a run with the 'Heist' and 'Photon' upgrades`
    },
    [MetaUnlockTag.RandomRicochet]: {
        name: 'Random Ricochet',
        isSteam: true,
        twistUnlock: 'randomRicochet',
        description: `Unlocks the 'Random Ricochet' twist`,
        icon: 'twist-random-ricochet',
        mission: `Beat a run with the 'Death On Rails' and '2-Way Split' upgrades`
    },
    [MetaUnlockTag.RunningOfTheShriekers]: {
      name: 'The Running of the Shriekers',
      isSteam: true,
      twistUnlock: 'runningOfTheShriekers',
      description: `Unlocks the 'Running of The Shriekers' twist`,
      icon: 'twist-running-of-the-shriekers',
      mission: `Defeat at least 200 Shriekers`
    },
    [MetaUnlockTag.RazorSharpSnowfall]: {
      name: 'Razor Sharp Snowfall',
      isSteam: true,
      twistUnlock: 'razorSharpSnowfall',
      description: `Unlocks the 'Razor Sharp Snowfall' twist`,
      icon: 'twist-razorshar-snowfall',
      mission: `Survive for at least 10 minutes on the 'Mountain' map`
    },
    [MetaUnlockTag.Tundra]: {
        name: 'Mountain Map',
        isSteam: true,
        mapUnlock: 'tundra',
        description: `Unlocks the 'Mountain' map`,
        icon: 'map-icon-mountain',
        mission: `Beat the 'Dreamer' boss 3 times`
    },
    [MetaUnlockTag.Story7]: {

    },
    [MetaUnlockTag.Story10]: {

    },
    [MetaUnlockTag.Story30]: {

    },
    [MetaUnlockTag.StartTwist]: {

    },
    [MetaUnlockTag.Gernerd]: {
        unlockPrimaryWeapon: undefined
    },

}

{
    for (const key in UnlockDefinitions) {
        const unlock = UnlockDefinitions[key]
        if (unlock.twistUnlock) {
            const mutatorDesc = MUTATOR_DEFINITIONS[unlock.twistUnlock].description
            const unlockDesc = `Twist unlock: ${mutatorDesc}`
            unlock.description = unlockDesc
        }
    }
}